import { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ClbLoading } from '@celebration/react';
import { ClbIcon } from '@celebration/react/ClbIcon/ClbIcon';
import IOffer from '../../../../../common/interfaces/offers/IOffer';
import PositionBadge from '../position-badge/position-badge';
import OfferCard from '../offer-card/offer-card';
import Countdown from '../../../../../common/components/countdown/countdown';
import i18n from '../../../../../lib/i18n';
import Position from '../../../../../common/enums/position';
import './position-section.scss';
import '../offers-list/offers-list.scss';
import { useOffersRound } from '../../../../../app/contexts/offers-round/offers-round.context';

interface IPositionSectionProps {
    position: Position;
    offersList: IOffer[];
    hasMore: boolean;
    onFetchMore: () => void;
    currentError?: string;
    confirmationBeginningTime?: Date | null;
    isConfirmationAvailable: boolean;
}

export default function PositionSection({
    position,
    offersList,
    hasMore,
    onFetchMore,
    currentError,
    confirmationBeginningTime,
    isConfirmationAvailable,
}: Readonly<IPositionSectionProps>) {
    const { fetchFirstPageByPosition, dispatch, roundState } = useOffersRound();

    const [isOpen, setIsOpen] = useState(false);
    const [hasOpenedFirstTime, setHasOpenedFirstTime] = useState(true);

    const toggleSection = () => {
        setIsOpen(s => {
            if (!s) dispatch({ type: 'FETCH_OFFERS_START', position });
            return !s;
        });
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleSection();
        }
    };

    const getEndMessage = () => {
        if (currentError) {
            return (
                <h2 data-testid={`position-${position}-error-message`}>
                    {i18n.t('CargasErroConsultar')}
                </h2>
            );
        }

        if (offersList.length > 0) {
            return (
                <h2 data-testid={`position-${position}-all-shown-message`}>
                    {i18n.t('CargasTodasJaExibidas')}
                </h2>
            );
        }

        return (
            <h2 data-testid={`position-${position}-no-offers-message`}>
                {i18n.t('CargasExibirNenhuma')}
            </h2>
        );
    };

    useEffect(() => {
        if (isOpen && hasOpenedFirstTime) {
            fetchFirstPageByPosition(position);
            setHasOpenedFirstTime(false);
        }
    }, [isOpen, fetchFirstPageByPosition, position, hasOpenedFirstTime]);

    useEffect(() => {
        if (roundState.status === 'filtering') {
            setIsOpen(false);
            setHasOpenedFirstTime(true);
        }
    }, [roundState.status]);

    return (
        <div
            className="position-section"
            data-testid={`position-section-${position}`}
        >
            <button
                className="position-section-header"
                onClick={toggleSection}
                onKeyDown={handleKeyDown}
                type="button"
                aria-expanded={isOpen}
                data-testid={`position-section-header-${position}`}
            >
                <div className="position-section-header-left">
                    <PositionBadge position={position} />
                    <span className="position-section-count">
                        {i18n.t(`SmartCargas.Rounds.Position${position}Title`)}
                    </span>
                </div>

                <ClbIcon
                    icon={isOpen ? 'chevron-up' : 'chevron-down'}
                    size="md"
                />
            </button>

            {confirmationBeginningTime && (
                <div
                    className={`position-status ${isConfirmationAvailable ? 'available' : 'countdown'}`}
                    data-testid={`position-status-${position}`}
                >
                    {!isConfirmationAvailable ? (
                        <>
                            <ClbIcon icon="time" size="sm" />
                            <span>
                                {i18n.t(
                                    'SmartCargas.Rounds.AvailableToConfirmIn',
                                )}
                            </span>
                            <Countdown
                                targetDateTime={confirmationBeginningTime}
                                countdownEndCallback={() => {}}
                            />
                        </>
                    ) : (
                        <>
                            <ClbIcon icon="checkbox-circle" size="sm" />
                            <span>
                                {i18n.t(
                                    'SmartCargas.Rounds.AlreadyAvailableToConfirm',
                                )}
                            </span>
                            <span>--:--</span>
                        </>
                    )}
                </div>
            )}

            {isOpen && (
                <div
                    className="position-section-content"
                    data-testid={`position-section-content-${position}`}
                >
                    {roundState.status === 'active' && (
                        <InfiniteScroll
                            className="offers-list"
                            dataLength={offersList.length}
                            next={onFetchMore}
                            hasMore={hasMore}
                            loader={<ClbLoading />}
                            endMessage={getEndMessage()}
                            scrollableTarget="offers-content"
                        >
                            {offersList.map(offer => (
                                <OfferCard
                                    key={offer.loadOfferId}
                                    offer={offer}
                                    isConfirmationEnabled={
                                        isConfirmationAvailable
                                    }
                                />
                            ))}
                        </InfiniteScroll>
                    )}
                    {(roundState.status === 'loadingOffers' ||
                        roundState.status === 'filtering') && (
                        <ClbLoading size="sm" />
                    )}
                </div>
            )}
        </div>
    );
}
